/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import {
    object,
    arrayOf,
    array,
    func,
    string,
    shape,
    bool,
    number,
} from 'prop-types';
import mbpLogger from 'mbp-logger';
import Grid from '@material-ui/core/Grid';

import { GRAPHQL_ENV } from '../../../../../../../gql';
import GraphqlPortalLoader from '../../../../../GraphqlCommonComponents/GraphqlPortalLoader';
import { getBrand } from '../../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import AddonDescBuilder from './AddonItemsDescBuilder';
import { addToCartDataType } from '../../../common/types';

const AddonItemsContainer = ({
    brand,
    product,
    product: {
        partNumber,
        brandId,
    },
    skuData,
    style,
    onCloseModal,
    categoryId,
    subscription,
    subscriptionDetails,
    presentation_family,
    disableAddToCart,
    isAddToCartEnabled,
    categoryPath,
    categoryName,
    selectedSku,
    updateWineStatus,
    productSkus,
    deliveryData,
    isMobile,
    handleMiniCartModalClick,
    mobileAgeVerificationOpen,
    handleMobileAgeVerification,
    addToCartData,
}) => {
    const ADD_ONS_QUERY = gql`
    query PDPpageQuery {
        findAddonsLegacy(brand: "${brand.domain}", environment: "${GRAPHQL_ENV}", id: "${partNumber}")
        {
            name, maxAllowedAddons, addons{partNumber,id, legacyId, name, shortDescription, image{path, name},prices{currency,type,value}, ageVerifyFlag}
        }
       }
    `;
    return (
        <Query query={ADD_ONS_QUERY}>
            {({ loading, error, data }) => {
                if (loading) {
                    return <GraphqlPortalLoader />;
                }

                if (error) {
                    mbpLogger.logError({
                        query: ADD_ONS_QUERY,
                        component: 'AddonItemsContainer.js',
                        message: 'Error loading data from Graphql',
                        env: GRAPHQL_ENV,
                        error,
                    });
                    return null;
                }

                if (!data) {
                    mbpLogger.logWarning({
                        query: ADD_ONS_QUERY,
                        component: 'AddonItemsContainer.js',
                        message: 'No data returned for query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }

                if (data <= 0) {
                    mbpLogger.logWarning({
                        query: ADD_ONS_QUERY,
                        component: 'AddonItemsContainer.js',
                        message: 'No content returned for query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }

                if (data?.findAddonsLegacy?.length === 0) {
                    return (
                        <div className={style.errorLoader}>
                            <p>Error! Unable to load Add ons</p>
                            {isMobile && (
                                <div
                                    className={style.closeAction}
                                    onClick={() => onCloseModal()}
                                    onKeyDown={() => { }}
                                    tabIndex="0"
                                    role="button"
                                >Close
                                </div>
                            )}
                        </div>
                    );
                }
                const [addons] = data.findAddonsLegacy;
                const maxAllowedAddons = addons?.maxAllowedAddons;

                return (
                    <Grid container>
                        <Grid item sm={12} className={isMobile ? style.addonDialog : style.wine}>
                            <AddonDescBuilder
                                handleMiniCartModalClick={handleMiniCartModalClick}
                                brand={brand}
                                maxAllowedAddons={maxAllowedAddons}
                                product={product}
                                addonsData={data.findAddonsLegacy}
                                skuData={skuData}
                                onCloseModal={onCloseModal}
                                brandId={brandId}
                                partNumber={partNumber}
                                categoryId={categoryId}
                                subscription={subscription}
                                subscriptionDetails={subscriptionDetails}
                                presentation_family={presentation_family}
                                disableAddToCart={disableAddToCart}
                                isAddToCartEnabled={isAddToCartEnabled}
                                categoryPath={categoryPath}
                                categoryName={categoryName}
                                selectedSku={selectedSku}
                                productSkus={productSkus}
                                updateWineStatus={updateWineStatus}
                                deliveryData={deliveryData}
                                isMobile={isMobile}
                                mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                                handleMobileAgeVerification={handleMobileAgeVerification}
                                addToCartData={addToCartData}
                            />
                        </Grid>
                    </Grid>
                );
            }}
        </Query>
    );
};

AddonItemsContainer.propTypes = {
    brand: shape({
        domain: string,
    }).isRequired,
    product: shape({
        id: string.isRequired,
        name: string.isRequired,
        brandId: string.isRequired,
        partNumber: string.isRequired,
    }).isRequired,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                }),
            ).isRequired,
        }),
    ),
    onCloseModal: func.isRequired,
    style: object, // Classes object
    categoryId: string.isRequired,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
    }),
    presentation_family: string.isRequired,
    disableAddToCart: bool,
    isAddToCartEnabled: bool,
    categoryPath: string.isRequired,
    categoryName: string.isRequired,
    selectedSku: shape({
        id: string.isRequired,
        minAge: string.isRequired,
    }).isRequired,
    productSkus: shape({
        minAge: string.isRequired,
    }).isRequired,
    deliveryData: shape({
        location_type_options: arrayOf(
            shape({
                name: string,
                value: string,
            }),
        ),
        show: bool,
        title: string,
        zip_code_title: string,
        location_type_title: string,
    }).isRequired,
    updateWineStatus: func.isRequired,
    handleMiniCartModalClick: func.isRequired,
    isMobile: bool,
    mobileAgeVerificationOpen: bool,
    handleMobileAgeVerification: func,
    addToCartData: addToCartDataType,
};

AddonItemsContainer.defaultProps = {
    style: {},
    skuData: [],
    isAddToCartEnabled: true,
    subscriptionDetails: {},
    disableAddToCart: false,
    subscription: {},
    isMobile: false,
    mobileAgeVerificationOpen: false,
    handleMobileAgeVerification: () => { },
    addToCartData: {},
};
const mapStateToProps = (state) => ({
    brand: getBrand(state),
});

export default connect(mapStateToProps)(AddonItemsContainer);
